import React, { useEffect } from 'react';
import { useGlobal } from '@rcg/ui-react';
import { Helmet } from 'react-helmet';
import { Typography } from '@rcg/ui-react/lib/components';
import ItineraryDetails from '../ItineraryDetails';
import useDictionary from '../../hooks/useDictionary';
import Dictionary from '../../utils/dictionary';
import getBrand from '../../utils/getBrand';
import { trackMaintenanceView } from '../../utils/analytics/browseTracking';
import { fillPhonePlaceholder } from '../../utils/maintenance';
import { MAINTENANCE_MODES } from '../../utils/constants';
import { bookingProfileVar } from '../../operations/localState/reactiveVariables';
import { ReactComponent as PlaneIcon } from '../../assets/svg/plane_up.svg';
import useStyles from './useStyles';

const TRACKING_BRAND = { rcl: 'RCI', cel: 'CEL' };
const MOBILE_MAX_WIDTH = 599;

const MaintenanceTakeover = ({ mode, phoneNumber }) => {
  const {
    data: { lang, country },
  } = useGlobal();

  useEffect(() => {
    trackMaintenanceView({
      brand: TRACKING_BRAND[getBrand().id],
      country: (country || '').toLowerCase(),
      language: (lang || '').toLowerCase(),
      siteType: window.innerWidth <= MOBILE_MAX_WIDTH ? 'mobile' : 'desktop',
    });
  }, []);

  const classes = useStyles();
  const getDictionary = useDictionary(Dictionary);
  const text = (entry) => fillPhonePlaceholder(getDictionary(entry.key), phoneNumber);
  const hasBooking = Boolean(bookingProfileVar()?.currentBooking?.sailing);

  const modeMessage =
    mode === MAINTENANCE_MODES.PLANNED_MIGRATION
      ? text(Dictionary.airMaintenanceTakeoverPlannedWindow)
      : text(Dictionary.airMaintenanceTakeoverUnplanned);

  const paragraphs = [
    { key: 'description', content: text(Dictionary.airMaintenanceTakeoverDescription) },
    { key: 'reassurance', content: text(Dictionary.airMaintenanceTakeoverReassurance) },
    { key: 'mode', content: modeMessage },
  ].filter(({ content }) => content && content.trim());

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
      {hasBooking && <ItineraryDetails />}
      <section
        className={classes.section}
        aria-labelledby="maintenance-takeover-title"
        data-testid="maintenance-takeover">
        <PlaneIcon className={classes.icon} aria-hidden="true" focusable="false" />
        <Typography
          id="maintenance-takeover-title"
          variant="h5"
          component="h1"
          className={classes.title}>
          {text(Dictionary.airMaintenanceTakeoverTitle)}
        </Typography>
        {paragraphs.map(({ key, content }) => (
          <Typography key={key} variant="subtitle1" className={classes.description}>
            {content}
          </Typography>
        ))}
      </section>
    </>
  );
};

export default MaintenanceTakeover;
